import { AnimatePresence } from 'framer-motion';
import { useMatch } from 'react-router-dom';
import { config } from '@/config';
import { useIsSchoolsMode } from '@/features/magic/hooks/useIsSchoolsMode';
import { getMagicLayerStyle } from '@/features/magic/magicBackground.lib';
import { useIsMagic } from '@/hooks/useIsMagic';
import { MotionBox, type MotionBoxProps } from '@/ui';

type State = Pick<
  ReturnType<typeof useIsMagic>,
  'artifactType' | 'isMagic' | 'isMagicLandingPage'
> & { isSchools: boolean; isSchoolsMode: boolean };

const isProduction =
  config.env === 'production' &&
  !config.isPreviewDeploy &&
  !config.isLocalProdBuild;

const calcKey = ({
  artifactType,
  isMagic,
  isMagicLandingPage,
  isSchools,
  isSchoolsMode,
}: State): string => {
  if (isMagic) {
    if (isSchoolsMode) {
      return 'magic:schools';
    }
    return `magic:${artifactType}`;
  }

  if (isMagicLandingPage) {
    return 'magic-page';
  }

  if (isSchools) {
    return 'schools';
  }

  return 'dashboard';
};

const calcProps = ({
  artifactType,
  isMagic,
  isMagicLandingPage,
  isSchools,
  isSchoolsMode,
}: State): MotionBoxProps => {
  if (isMagic) {
    return { layerStyle: getMagicLayerStyle(artifactType, isSchoolsMode) };
  }

  if (isMagicLandingPage) {
    return {
      layerStyle: 'magicPageBackground',
    };
  }

  if (isSchools && isProduction) {
    return { bg: '#FAF6F0' };
  }

  return {};
};

export const Background = () => {
  const isSchools = useMatch('/internal/schools') !== null;
  const { isSchoolsMode } = useIsSchoolsMode();
  const state = { ...useIsMagic(), isSchools, isSchoolsMode };

  return (
    <AnimatePresence initial={false} mode="popLayout">
      <MotionBox
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        initial={{ opacity: 0 }}
        inset="0"
        key={calcKey(state)}
        pos="fixed"
        transition={{ duration: 0.3 }}
        zIndex="-1"
        {...calcProps(state)}
      />
    </AnimatePresence>
  );
};
